import { ReactNode, useEffect } from "react";
import {
  AuthenticatedTemplate,
  UnauthenticatedTemplate,
  useMsal,
} from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";

import { apiScopes } from "@/lib/auth";

interface Props {
  children: ReactNode;
}

/**
 * MSAL-backed auth gate. Redirects to Entra on mount if no account is
 * signed in; while interaction is in flight, shows a branded splash.
 */
export function AuthGate({ children }: Props) {
  const { instance, inProgress } = useMsal();

  useEffect(() => {
    if (inProgress !== InteractionStatus.None) return;
    const hasAccount = instance.getAllAccounts().length > 0;
    if (!hasAccount) {
      instance.loginRedirect({
        ...apiScopes,
        prompt: "select_account",
        extraQueryParameters: { domain_hint: "unitymsk.com" },
      });
    }
  }, [instance, inProgress]);

  return (
    <>
      <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
      <UnauthenticatedTemplate>
        <Splash />
      </UnauthenticatedTemplate>
    </>
  );
}

function Splash() {
  return (
    <div className="flex h-screen items-center justify-center bg-paper-1 text-ink-3">
      <div className="text-center">
        <div className="animate-pulse font-serif italic text-2xl text-teal-500">Flow</div>
        <div className="mt-2 text-sm">Signing you in…</div>
      </div>
    </div>
  );
}
